import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Dropdown, Flex, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, ImportOutlined, LockOutlined, PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminChannels } from '../../hooks/useAdminChannels';
import { useChannelCategories } from '../../hooks/useChannelCategories';
import AdminChannelFormModal, { AdminChannelFormValues } from '../../components/AdminChannelFormModal/AdminChannelFormModal.component';
import ChannelCategoriesModal from '../../components/ChannelCategoriesModal/ChannelCategoriesModal.component';
import { ChannelEntity } from '../../../domain/entities/Channel';
import { ChannelType } from '../../../shared/types/Channel';
import { NO_CATEGORY_SLUG } from '../../../shared/types/ChannelCategory';

import './AdminChannelsPage.styles.css';
import { channelRulesEqual } from '../../../domain/entities/ChannelRule';

const TYPE_TAG_COLORS: Record<ChannelType, string> = {
  [ChannelType.PAID]: 'gold',
  [ChannelType.FREE]: 'green',
  [ChannelType.DEACTIVATED]: 'default',
};

const shortLink = (link: string) => link.replace(/^https:\/\/t\.me\//, '');

const sendingWindowChanges = (channel: ChannelEntity, values: AdminChannelFormValues) => {
  const { start, end } = channel.sendingWindow;
  const changed = values.sendingStartTime !== start || values.sendingEndTime !== end;
  return changed ? { sendingStartTime: values.sendingStartTime, sendingEndTime: values.sendingEndTime } : {};
};

const AdminChannelsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const {
    items, total, page, perPage, filters, isLoading, isSaving, error,
    setFilters, setPage, createChannel, updateChannel, createCategory, renameCategory,
  } = useAdminChannels();
  const { categories } = useChannelCategories();

  const [editing, setEditing] = useState<ChannelEntity | undefined>();
  const [isFormOpen, setFormOpen] = useState(false);
  const [isCategoriesOpen, setCategoriesOpen] = useState(false);

  const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const typeLabels: Record<ChannelType, string> = {
    [ChannelType.FREE]: t('channel-type-free', { ns: 'labels' }),
    [ChannelType.PAID]: t('channel-type-paid', { ns: 'labels' }),
    [ChannelType.DEACTIVATED]: t('channel-type-deactivated', { ns: 'labels' }),
  };

  const showError = (e: unknown) => {
    const slug = e instanceof Error ? e.message : 'unknown-error';
    message.error(t(slug, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) }));
  };

  const runMutation = async (action: () => Promise<void>, successKey: string): Promise<boolean> => {
    try {
      await action();
      message.success(t(successKey, { ns: 'messages' }));
      return true;
    } catch (e) {
      showError(e);
      return false;
    }
  };

  const openCreate = () => { setEditing(undefined); setFormOpen(true); };
  const openEdit = (channel: ChannelEntity) => { setEditing(channel); setFormOpen(true); };

  const handleSubmit = async (values: AdminChannelFormValues) => {
    const saved = editing
      ? await runMutation(() => updateChannel(editing.uuid, {
        type: values.type,
        categoryUuid: values.categoryUuid ?? '',
        membersCount: values.membersCount,
        minimalSendingInterval: values.minimalSendingInterval,
        isPrivate: values.isPrivate,
        rules: channelRulesEqual(values.rules ?? [], editing.rules) ? undefined : values.rules ?? [],
        ...sendingWindowChanges(editing, values),
      }), 'channel-updated')
      : await runMutation(() => createChannel(values), 'channel-created');
    if (saved) setFormOpen(false);
  };

  const changeType = (channel: ChannelEntity, type: ChannelType) => runMutation(
    () => updateChannel(channel.uuid, { type }),
    type === ChannelType.DEACTIVATED ? 'channel-deactivated' : 'channel-activated',
  );

  const columns: ColumnsType<ChannelEntity> = [
    {
      title: t('channel-link', { ns: 'labels' }),
      dataIndex: 'link',
      render: (_, channel) => (
        <Space size={6}>
          {channel.isPrivate && (
            <Tooltip title={t('channel-private', { ns: 'labels' })}>
              <LockOutlined className="admin-channels-private-icon" />
            </Tooltip>
          )}
          <a href={channel.link} target="_blank" rel="noreferrer">{shortLink(channel.link)}</a>
        </Space>
      ),
    },
    {
      title: t('channel-category', { ns: 'labels' }),
      render: (_, channel) => channel.category
        ? channel.category.name
        : <Typography.Text type="secondary">{t('channel-no-category', { ns: 'labels' })}</Typography.Text>,
    },
    {
      title: t('channel-type', { ns: 'labels' }),
      width: 140,
      render: (_, channel) => <Tag color={TYPE_TAG_COLORS[channel.type]}>{typeLabels[channel.type]}</Tag>,
    },
    {
      title: t('channel-members-count', { ns: 'labels' }),
      align: 'right',
      width: 140,
      render: (_, channel) => numberFormat.format(channel.membersCount),
    },
    {
      title: t('channel-sending-interval', { ns: 'labels' }),
      align: 'right',
      width: 170,
      render: (_, channel) => t('channel-sending-interval-value', { ns: 'labels', count: channel.minimalSendingInterval }),
    },
    {
      title: t('channel-actions', { ns: 'labels' }),
      width: 200,
      render: (_, channel) => (
        <Space size={4}>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(channel)} aria-label={t('edit', { ns: 'buttons' })} />
          {channel.type === ChannelType.DEACTIVATED ? (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: ChannelType.FREE, label: t('activate-as-free', { ns: 'buttons' }) },
                  { key: ChannelType.PAID, label: t('activate-as-paid', { ns: 'buttons' }) },
                ],
                onClick: ({ key }) => changeType(channel, key as ChannelType),
              }}
            >
              <Button type="link" disabled={isSaving}>{t('activate', { ns: 'buttons' })}</Button>
            </Dropdown>
          ) : (
            <Popconfirm
              title={t('channel-deactivate-confirm-title', { ns: 'modals' })}
              description={t('channel-deactivate-confirm', { ns: 'modals' })}
              okText={t('deactivate', { ns: 'buttons' })}
              cancelText={t('cancel', { ns: 'buttons' })}
              okButtonProps={{ danger: true }}
              onConfirm={() => changeType(channel, ChannelType.DEACTIVATED)}
            >
              <Button type="link" danger disabled={isSaving}>{t('deactivate', { ns: 'buttons' })}</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const categoryOptions = [
    ...categories.map((c) => ({ value: c.slug, label: c.name })),
    { value: NO_CATEGORY_SLUG, label: t('channel-no-category', { ns: 'labels' }) },
  ];

  return (
    <div className="admin-channels-page">
      <Flex className="admin-channels-header" justify="space-between" align="flex-start" wrap="wrap" gap={16}>
        <div>
          <h1 className="admin-channels-title">{t('admin-channels', { ns: 'titles' })}</h1>
          <p className="admin-channels-subtitle">{t('admin-channels-subtitle', { ns: 'titles' })}</p>
        </div>
        <Space wrap>
          <Button icon={<TagsOutlined />} onClick={() => setCategoriesOpen(true)}>{t('manage-categories', { ns: 'buttons' })}</Button>
          <Button icon={<ImportOutlined />} onClick={() => navigate('/admin/channels/import')}>{t('channel-import-open', { ns: 'buttons' })}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('add-channel', { ns: 'buttons' })}</Button>
        </Space>
      </Flex>

      <Flex className="admin-channels-filters" gap={12} wrap="wrap">
        <Input.Search
          allowClear
          className="admin-channels-search"
          placeholder={t('search-channels', { ns: 'labels' })}
          defaultValue={filters.search}
          onSearch={(search) => setFilters({ ...filters, search })}
        />
        <Select
          mode="multiple"
          allowClear
          className="admin-channels-filter"
          placeholder={t('channel-filters-categories', { ns: 'labels' })}
          value={filters.categories}
          options={categoryOptions}
          optionFilterProp="label"
          maxTagCount="responsive"
          onChange={(categorySlugs: string[]) => setFilters({ ...filters, categories: categorySlugs })}
        />
        <Select
          mode="multiple"
          allowClear
          className="admin-channels-filter"
          placeholder={t('channel-filters-type', { ns: 'labels' })}
          value={filters.types}
          options={Object.values(ChannelType).map((type) => ({ value: type, label: typeLabels[type] }))}
          maxTagCount="responsive"
          onChange={(types: ChannelType[]) => setFilters({ ...filters, types })}
        />
      </Flex>

      {error && <Alert type="error" showIcon message={t(error, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) })} />}

      <Table<ChannelEntity>
        rowKey={(channel) => channel.uuid}
        columns={columns}
        dataSource={items}
        loading={isLoading}
        scroll={{ x: 960 }}
        rowClassName={(channel) => (channel.type === ChannelType.DEACTIVATED ? 'admin-channels-row-deactivated' : '')}
        locale={{ emptyText: t('no-channels-for-filters', { ns: 'labels' }) }}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          showSizeChanger: true,
          showTotal: (count) => t('channels-total', { ns: 'labels', count }),
          onChange: (nextPage, nextPerPage) => setPage(nextPage, nextPerPage),
        }}
      />

      <AdminChannelFormModal
        open={isFormOpen}
        channel={editing}
        categories={categories}
        isSaving={isSaving}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <ChannelCategoriesModal
        open={isCategoriesOpen}
        categories={categories}
        isSaving={isSaving}
        onClose={() => setCategoriesOpen(false)}
        onCreate={(name) => runMutation(() => createCategory(name), 'category-created')}
        onRename={(uuid, name) => runMutation(() => renameCategory(uuid, name), 'category-renamed')}
      />
    </div>
  );
};

export default AdminChannelsPage;
